import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import React, { FC, useCallback, useMemo, useState } from 'react';

import { Dropdown, DropdownOption } from '@column/column-ui-kit';

import { useDebounce } from '~/hooks/useDebounce';
import { FeatureFlag, useFeatureFlag } from '~/lib/flags';
import { Loan, LoanRepository } from '~/repositories';
import { useSessionStore } from '~/stores/Session';

const loanLabel = (loan: Loan) => loan.description || loan.id;

const loanDropdownListQueryKey = (platformId?: string, isSandbox?: boolean, query?: string) =>
  ['loanDropdown', 'list', platformId, isSandbox, query] as const;
const loanQueryKey = (id: string, platformId?: string, isSandbox?: boolean) =>
  ['loans', 'get', id, platformId, isSandbox] as const;

interface UseLoanOptionsConfig {
  value?: string | null;
}

export const useLoanOptions = (config: UseLoanOptionsConfig = {}) => {
  const { value } = config;
  const platformId = useSessionStore((state) => state.currentPlatform?.id);
  const isSandbox = useSessionStore((state) => state.isSandbox);
  const resolvedId = value ?? undefined;
  const isLoanSearchEnabled = useFeatureFlag(FeatureFlag.EnableLoanSearch);
  const [searchText, setSearchText] = useState<string | undefined>();
  const effectiveSearchText = isLoanSearchEnabled ? searchText : undefined;

  const loansQuery = useInfiniteQuery({
    queryKey: loanDropdownListQueryKey(platformId, isSandbox, effectiveSearchText),
    queryFn: ({ pageParam }: { pageParam?: string }) =>
      LoanRepository.getAll({ startingAfter: pageParam, query: effectiveSearchText }),
    getNextPageParam: (lastPage) => {
      if (!lastPage.hasMore) return undefined;
      return lastPage.loans[lastPage.loans.length - 1]?.id;
    },
    initialPageParam: undefined as string | undefined,
    placeholderData: keepPreviousData,
    staleTime: 15_000,
  });

  const loans = useMemo(() => {
    const entries = loansQuery.data?.pages.flatMap((page) => page.loans) ?? [];
    return [...new Map(entries.map((entry) => [entry.id, entry])).values()];
  }, [loansQuery.data]);

  const activeLoanQuery = useQuery({
    queryKey: loanQueryKey(resolvedId!, platformId, isSandbox),
    queryFn: () => LoanRepository.get(resolvedId!),
    enabled: Boolean(resolvedId),
    placeholderData: () => loans.find((loan) => loan.id === resolvedId),
    staleTime: 5 * 60_000,
  });

  const activeLoan = activeLoanQuery.data;
  const [search] = useDebounce((query?: string) => setSearchText(query || undefined), 300, []);

  const toOption = useCallback(
    (loan: Loan): DropdownOption => ({
      label: loanLabel(loan),
      value: loan.id,
    }),
    []
  );

  const options = useMemo(() => loans.map(toOption), [loans, toOption]);

  const hiddenOptions = useMemo(() => {
    if (!resolvedId) return [];

    const selected =
      loans.find((loan) => loan.id === resolvedId) || (activeLoan?.id === resolvedId ? activeLoan : undefined);

    return selected ? [toOption(selected)] : [];
  }, [activeLoan, resolvedId, loans, toOption]);

  const { fetchNextPage, hasNextPage, isFetchingNextPage, isPlaceholderData } = loansQuery;
  const handleScrollEnd = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage && !isPlaceholderData) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, isPlaceholderData]);

  return {
    options,
    hiddenOptions,
    handleScrollEnd,
    search,
    loans,
  };
};

export interface LoanDropdownProps {
  value?: string | null;
  onChange: (value: string) => void;
  fullWidth?: boolean;
  maxWidth?: string;
  hasError?: boolean;
  placeholder?: string;
  isDisabled?: boolean;
}

export const LoanDropdown: FC<LoanDropdownProps> = ({
  value,
  onChange,
  fullWidth,
  maxWidth,
  hasError,
  placeholder,
  isDisabled,
}) => {
  const isLoanSearchEnabled = useFeatureFlag(FeatureFlag.EnableLoanSearch);
  const { options, hiddenOptions, handleScrollEnd, search } = useLoanOptions({ value });

  return (
    <Dropdown
      options={options}
      hiddenOptions={hiddenOptions}
      active={value ?? undefined}
      fullWidth={fullWidth}
      maxWidth={maxWidth}
      emptyMessage="No loans found"
      onSearchChange={isLoanSearchEnabled ? (query: string) => search(query) : undefined}
      onChange={onChange}
      onScrollEnd={handleScrollEnd}
      hasError={hasError}
      isDisabled={isDisabled}
      placeholder={placeholder}
      search={isLoanSearchEnabled}
    />
  );
};
